import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, TrendingUp, Building2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

function RankList({ title, badge, Icon, items }) {
  return (
    <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
      <div className="px-5 pt-4 pb-3 border-b border-[#E5DCC4]">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]"><Icon size={12}/> {badge}</div>
        <h3 className="mt-1 font-display text-lg font-semibold text-[#1A2622]">{title}</h3>
      </div>
      <ol>
        {items.map((it, i) => (
          <li key={i}>
            <Link to={it.to} className="flex items-center justify-between px-5 py-3 border-t border-[#F0E7D2] first:border-t-0 hover:bg-[#FBF6EC] group">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-[11px] font-mono text-[#A8B0AA] w-5">{String(i+1).padStart(2,'0')}</span>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-[#1A2622] truncate group-hover:text-[#2D5F4F]">{it.title}</div>
                  <div className="text-[10.5px] text-[#7A857F] mt-0.5">{it.subtitle}</div>
                </div>
              </div>
              <span className="text-[12px] font-semibold text-[#1A2622] shrink-0">{it.right}</span>
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function RankingsPage() {
  const [data, setData] = useState({ top_facilities: [], top_owners: [], top_chains: [] });
  useEffect(() => { api.rankings().then(setData).catch(() => {}); }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Rankings' }]} />

        <div className="mt-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
            <Award size={12} className="text-[#4A7C5E]"/>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">CMS Rankings</span>
          </div>
          <h1 className="mt-4 font-display text-4xl font-semibold text-[#1A2622] tracking-tight">Leaderboards</h1>
          <p className="mt-3 text-[15px] text-[#3A4A44] max-w-3xl">CMS-published rankings across facilities, owners, and chains — sorted by rating and portfolio size.</p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RankList title="Largest owners by portfolio" badge="Owners" Icon={Users}
            items={(data.top_owners || []).map(o => ({ to:`/owners/${o.slug}`, title:o.name, subtitle:`${o.states} ${o.states===1?'state':'states'} · ${o.type || ''}`, right:`${o.facilities}` }))}/>
          <RankList title="Biggest chain organizations" badge="Chains" Icon={TrendingUp}
            items={(data.top_chains || []).map(c => ({ to:`/chains/${c.slug}`, title:c.name, subtitle:c.rating ? `Rating ${c.rating}` : 'Multi-facility operator', right:`${c.facilities}` }))}/>
          <RankList title="Top-rated facilities" badge="Facilities" Icon={Award}
            items={(data.top_facilities || []).map(f => ({ to:`/facilities/${f.slug}`, title:f.name, subtitle:`${f.city || ''}${f.city && f.state ? ', ' : ''}${f.state || ''}`, right:`★ ${f.rating}` }))}/>
        </div>
      </main>
      <Footer />
    </div>
  );
}
